import { useRef, useState } from 'react';
import { ModuleListView } from '../modules/ModuleListView';
import { useNotify } from '../../components/notifications';
import { api } from '../../api/client';
import { runLoading } from '../../components/loading';
import { FormTitle } from '../../components/FormTitle';
import { AppToolbar } from '../../components/AppToolbar/AppToolbar';
import { MailSetupDialog, ServicePeriodDialog } from './EdiDialogs';

// ─────────────────────────────────────────────────────────────────────────
// External Data Import — main 3-tab form (Phase 2)
// ─────────────────────────────────────────────────────────────────────────
//   Faithful port of the legacy ALPACA "External Data Import" DashboardView:
//     • Tab 1 — Scheduled Sample Results (dbo.ScheduledSampleResults)
//     • Tab 2 — Sample Results          (dbo.ImportSuboutSampleResults)
//     • Tab 3 — Alert Mail Log          (dbo.tbl_EmailAlertMailDetails)
//   Each tab embeds the shared <ModuleListView> so it gets server-side paging
//   (Sample Results is 117k rows), per-column filters, sort, export and the
//   standard footer for free.
//
//   The EDI-specific toolbar actions sit in the action bar above the tabs:
//     • Import        — upload + parse an external-lab Excel file (wired with
//                       the configurable Sheet Mapping settings — next step).
//     • Mail Setup    — popup grid of the 4 alert events (ALPACA parity).
//     • Service Period— popup scheduled-run form (ALPACA parity).
//     • Check Status  — re-evaluate MCL/SCL/Alert/MRL flags.
//     • Send Mail     — send limit-exceeded alert emails.
//   Per-tab Refresh / Export to live on each grid's own toolbar (no duplication).
// ─────────────────────────────────────────────────────────────────────────

interface Props {
  onHome: () => void;
}

type TabKey = 'edi-scheduled-results' | 'edi-sample-results' | 'edi-alert-mail-log';

const TABS: { key: TabKey; label: string }[] = [
  { key: 'edi-scheduled-results', label: 'Scheduled Sample Results' },
  { key: 'edi-sample-results',    label: 'Sample Results' },
  { key: 'edi-alert-mail-log',    label: 'Alert Mail Log' },
];

export default function ExternalDataImportPage({ onHome }: Props) {
  const notify = useNotify();
  const [tab, setTab] = useState<TabKey>('edi-scheduled-results');
  // Bumped after a successful import to force the active grid to remount + refetch.
  const [reloadKey, setReloadKey] = useState(0);
  const fileInput = useRef<HTMLInputElement>(null);
  // Toolbar popups (ALPACA parity).
  const [mailOpen, setMailOpen]       = useState(false);
  const [serviceOpen, setServiceOpen] = useState(false);

  // Send Mail — email the flagged-but-not-notified results via Mail Setup.
  const sendMail = async () => {
    try {
      const res = await runLoading(
        api.post<{ data?: { sent: number; failed: number; remaining: number; messages: string[] } }>(
          '/api/v1/external-data-import/send-mail'),
        { blocking: true, label: 'Sending limit alerts…' });
      const d = res.data?.data;
      if (d) {
        if (d.sent === 0 && d.failed === 0) {
          notify.info(d.messages?.[0] ?? 'No alert emails to send.');
        } else if (d.failed > 0) {
          notify.warning(`Sent ${d.sent}, failed ${d.failed}${d.remaining ? `, ${d.remaining} remaining` : ''}. Check Mail Setup SMTP settings.`);
        } else {
          notify.success(`Sent ${d.sent} alert email(s)${d.remaining ? `, ${d.remaining} remaining` : ''}.`);
        }
      }
    } catch (e: unknown) {
      notify.error(
        (e as { response?: { data?: { error?: { message?: string } } } })
          .response?.data?.error?.message
        ?? (e as { message?: string }).message ?? 'Send Mail failed.');
    }
  };

  // Check Status — re-evaluate imported results against the MCL/SCL/Alert/MRL
  // limits and set exceedance flags, then refresh the grid.
  const checkStatus = async () => {
    try {
      const res = await runLoading(
        api.post<{ data?: { mcl: number; scl: number; alert: number; mrl: number; limitsLoaded: number; messages: string[] } }>(
          '/api/v1/external-data-import/check-status'),
        { blocking: true, label: 'Checking limit status…' });
      const d = res.data?.data;
      if (d) {
        if (d.limitsLoaded === 0) {
          notify.warning(d.messages?.[0] ?? 'No limits loaded. Import the limits spreadsheet first.');
        } else {
          const total = d.mcl + d.scl + d.alert + d.mrl;
          if (total > 0) {
            notify.success(`Flagged ${total} exceedance(s) — MCL ${d.mcl}, SCL ${d.scl}, Alert ${d.alert}, MRL ${d.mrl}.`);
            setTab('edi-sample-results');
            setReloadKey(k => k + 1);
          } else {
            notify.info('No new limit exceedances found.');
          }
        }
      }
    } catch (e: unknown) {
      notify.error(
        (e as { response?: { data?: { error?: { message?: string } } } })
          .response?.data?.error?.message
        ?? (e as { message?: string }).message ?? 'Check Status failed.');
    }
  };

  // Import: upload an external-lab Excel workbook → server parses via the active
  // Sheet Mappings → upserts the import tables → refresh the grid.
  const onPickFile = () => fileInput.current?.click();
  const onFileChosen = async (ev: React.ChangeEvent<HTMLInputElement>) => {
    const file = ev.target.files?.[0];
    ev.target.value = '';            // allow re-selecting the same file later
    if (!file) return;
    const form = new FormData();
    form.append('file', file);
    try {
      const res = await runLoading(
        // Let axios set multipart/form-data + boundary automatically from FormData.
        api.post<{ data?: { sampleResults: number; scheduled: number; sheetsMatched: number; sheetsSkipped: number; messages: string[] } }>(
          '/api/v1/external-data-import/import', form),
        { blocking: true, label: `Importing ${file.name}…` });
      const d = res.data?.data;
      if (d) {
        if (d.sheetsMatched === 0) {
          notify.warning(d.messages?.[0] ?? 'No worksheet matched an active Sheet Mapping.');
        } else {
          notify.success(`Imported ${d.sampleResults} sample result(s) and ${d.scheduled} scheduled row(s) from ${d.sheetsMatched} sheet(s).`);
          setTab('edi-sample-results');
          setReloadKey(k => k + 1);
        }
      }
    } catch (e: unknown) {
      notify.error(
        (e as { response?: { data?: { error?: { message?: string } } } })
          .response?.data?.error?.message
        ?? (e as { message?: string }).message ?? 'Import failed.');
    }
  };


  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', background: 'var(--ai-bg)' }}>
      {/* Header + action bar */}
      <div style={{ padding: '14px 20px 8px' }}>
        <div style={{ fontSize: 11, color: 'var(--ai-muted)', marginBottom: 4 }}>
          Home &nbsp;›&nbsp; Reporting &nbsp;›&nbsp;
          {/* token-exempt: #1F4E79 = reporting breadcrumb navy (semantic, no --ai-* token). */}
          <span style={{ color: '#1F4E79', fontWeight: 600 }}>External Data Import</span>
        </div>
        {/* Shared title (nav icon/colour + load-time) — single source of truth. */}
        <FormTitle pageId="edi-import" />
        <input ref={fileInput} type="file" accept=".xls,.xlsx,.xlsm" style={{ display: 'none' }}
          onChange={onFileChosen} />
        {/* Shared action bar — form-specific import actions in the left slot. */}
        <div style={{ marginTop: 10 }}>
          <AppToolbar left={
            <>
              <button className="ca-btn" onClick={onPickFile}><i className="ti ti-upload" /> Import</button>
              <button className="ca-btn" onClick={checkStatus}><i className="ti ti-checkup-list" /> Check Status</button>
              <button className="ca-btn" onClick={sendMail}><i className="ti ti-send" /> Send Mail</button>
              <button className="ca-btn" onClick={() => setMailOpen(true)}><i className="ti ti-mail-cog" /> Mail Setup</button>
              <button className="ca-btn" onClick={() => setServiceOpen(true)}><i className="ti ti-clock-cog" /> Service Period</button>
            </>
          } />
        </div>
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', gap: 2, padding: '0 20px', borderBottom: '1px solid var(--ai-line)' }}>
        {TABS.map(t => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            style={{
              padding: '8px 16px', fontSize: 13, fontWeight: tab === t.key ? 700 : 500,
              /* token-exempt: #1F4E79 = active-tab navy (semantic reporting accent, no --ai-* token). */
              color: tab === t.key ? '#1F4E79' : 'var(--ai-muted)',
              background: tab === t.key ? '#fff' : 'transparent',
              border: '1px solid var(--ai-line)', borderBottom: tab === t.key ? '1px solid #fff' : '1px solid var(--ai-line)',
              borderRadius: '6px 6px 0 0', marginBottom: -1, cursor: 'pointer',
            }}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* Active tab grid — keyed so the embedded ModuleListView remounts per tab
          (each module has its own range/filter/paging state). */}
      <div style={{ flex: 1, minHeight: 0, overflow: 'hidden' }}>
        <ModuleListView
          key={`${tab}-${reloadKey}`}
          moduleKey={tab}
          group="External Data Import"
          navTitle={TABS.find(t => t.key === tab)!.label}
          onHome={onHome}
        />
      </div>

      {/* ALPACA-style toolbar popups */}
      {mailOpen    && <MailSetupDialog    onClose={() => setMailOpen(false)} />}
      {serviceOpen && <ServicePeriodDialog onClose={() => setServiceOpen(false)} />}
    </div>
  );
}
